import {
  ChangeDetectionStrategy,
  Component,
  ViewEncapsulation,
  computed,
  inject,
  input
} from '@angular/core';
import { OSKColorVariant, OSKSize } from '../../types/osk-theme.types';
import { OSK_UI_LABELS } from '../../core/config/osk-ui-config';

/**
 * Atom: <osk-spinner>
 * Animated SVG loading indicator supporting all 9 semantic color variants and 3 sizes.
 */
@Component({
  selector: 'osk-spinner',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrl: './osk-spinner.component.scss',
  template: `
    <span [class]="spinnerClasses()" role="status" [attr.aria-label]="text().loading">
      <svg
        [attr.width]="pixelSize()"
        [attr.height]="pixelSize()"
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden="true"
      >
        <circle
          cx="12"
          cy="12"
          r="9"
          stroke="currentColor"
          stroke-opacity="0.22"
          stroke-width="2.5"
        />
        <path
          d="M21 12a9 9 0 0 0-9-9"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
        />
      </svg>
    </span>
  `
})
export class OSKSpinnerComponent {
  private readonly labels = inject(OSK_UI_LABELS);
  /** Every string this component renders: an input when given, else the configured label. */
  protected readonly text = computed(() => {
    const labels = this.labels().spinner;
    return {
      loading: this.label() ?? labels.loading
    };
  });

  readonly color = input<OSKColorVariant>('primary');
  readonly size = input<OSKSize>('md');
  readonly label = input<string | undefined>(undefined);

  protected readonly spinnerClasses = computed<string>(() => {
    return `osk-spinner osk-spinner--${this.color()} osk-spinner--${this.size()}`;
  });

  protected readonly pixelSize = computed<number>(() => {
    const s = this.size();
    if (s === 'sm') {
      return 16;
    }
    if (s === 'lg') {
      return 28;
    }
    return 20;
  });
}
